import { type ReactElement, type ReactNode } from 'react';
import IconAction, { type IconActionProps } from '~/components/new/IconAction';
import '~/styles/components/ui/label.scss';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles';
import Icon, { RemixIconNames, type IconNames } from './Icon';
import { SwitchInput, type SwitchProps } from './Switch';

type variants = 'overline' | 'strong' | 'subtle';
export type LabelProps = {
    // variant changes the style of the label, overline shows a uppercase text for specific ocasions, mostly as table column header.
    variant?: variants;
    // the icon at the left side of the label.
    icon?: IconNames;
    // info renders an info icon to the right of the label, with a tooltip.
    info?: string;
    // dot renders a small dot at the left side of the label, next to the icon. Used when something is new or relevant.
    dot?: boolean;
    // caption adds a dimmed text at the right side of the label, used in forms to show when the field is optional.
    caption?: string;
    // description renders text below the label, used to provide additional context.
    description?: string | ReactNode;
    // toggle specifies if the label should have a toggle switch at the right side.
    toggle?: boolean;
    // toggleValue is the current value of the toggle switch.
    toggleValue?: boolean;
    // toggleDisabled prevent interacting with the switch, used from form label.
    toggleDisabled?: boolean;
    // onToggle is the callback function that will be called when toggle changes.
    onToggle?: SwitchProps['onChange'];
    // iconAction is a IconAction component that will be rendered at the right side of the label, use it for actions like edit or delete.
    iconAction?: ReactElement<IconActionProps>;
    className?: string;
    children?: ReactNode;
} & DataTestAttributes;

export default function Label({
    variant = 'strong',
    children,
    icon,
    caption,
    info,
    dot = false,
    description,
    toggle = false,
    toggleValue = false,
    toggleDisabled = false,
    onToggle,
    iconAction,
    className = '',
    ['data-test-component']: testComponent = 'ui/Label',
    ['data-test-name']: testName,
}: LabelProps) {
    return (
        <div
            className={classNames({
                label: true,
                [className]: Boolean(className),
            })}
            data-test-component={testComponent}
            data-test-name={testName}>
            <div
                role='button'
                onClick={() => {
                    onToggle?.(!toggleValue);
                }}
                className={classNames({
                    'label-header': true,
                    'cursor-pointer': toggle,
                    [`label-header-${variant}`]: true,
                })}>
                <div className={classNames({ 'label-header-text': true, dot: Boolean(dot) })}>
                    {icon && <Icon icon={icon} />}
                    {children}
                    {caption && <span className='label-header-caption'>{caption}</span>}
                    {info && (
                        <IconAction
                            title={info}
                            icon={RemixIconNames.RiInformationLine}
                            colorVar='core-tertiary'
                        />
                    )}
                </div>
                {toggle && (
                    <SwitchInput
                        className='label-header-switch'
                        checked={toggleValue}
                        disabled={toggleDisabled}
                        data-test-name={testName}
                        readOnly
                    />
                )}
                {iconAction}
            </div>
            {description && <span className='label-header-description'>{description}</span>}
        </div>
    );
}

Label.Skeleton = function LabelSkeleton({ descriptionLines = 0 }: { descriptionLines?: number }) {
    return (
        <div className='label label-skeleton'>
            <div className='title'></div>
            {Array.from({ length: descriptionLines }).map((_, i) => (
                <div
                    key={i}
                    className='description-line'></div>
            ))}
        </div>
    );
};

Label.SkeletonTitle = function LabelSkeletonTitle({
    className,
    align = 'left',
    width,
}: {
    className?: string;
    align?: 'left' | 'center';
    width?: number;
}) {
    return (
        <div className={`label label-skeleton items-${align}`}>
            <div
                className={`title ${className || ''}`}
                style={{ width: width ? `${width}px` : '30%' }}></div>
        </div>
    );
};

Label.SkeletonLine = function LabelSkeletonLine({
    className,
    align = 'left',
    height,
    width,
}: {
    className?: string;
    align?: 'left' | 'center';
    height?: number;
    width?: number;
}) {
    return (
        <div className={`label label-skeleton items-${align}`}>
            <div
                className={`description-line ${className || ''}`}
                style={{
                    width: width ? `${width}px` : '100%',
                    height: height ? `${height}px` : undefined,
                }}></div>
        </div>
    );
};
